import { computed, reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import { useSessionStorage } from '@vueuse/core'
import { convertTreeRouterToArr } from '@/common/util'
import gruposDePermissaoService from '@/services/gruposDePermissao.service'
import { permissionGroupService } from '@/services/gruposDePermissao.service'
import webStorage from '@/common/webStorage'
import useUsuariosStore from './usuariosStore'

import type { Router } from 'vue-router'
import type { GrupoPermissaoType, GrupoPermissaoForm, UserPermissionType, LoaderType } from '@/types/types'
import useAuthStore from './authStore'

export const useGrupoDePermissoes = defineStore('grupoDePermissoes', () => {
  /**
   * States
   */
  const list = ref<GrupoPermissaoType[]>([])

  const newList = useSessionStorage<any>('permGroup', {
    status: 0,
    groups: [],
    users: [],
    lastUpdate: 0
  })

  const permissions = ref<UserPermissionType[]>(webStorage.getItem('permissions') ?? [])

  const nStatus = reactive<Record<string, LoaderType>>({
    getUserPermissions: 'initial',
    create: 'initial',
    update: 'initial',
    getAll: 'initial',
    getById: 'initial',
    delete: 'initial',
    setPermission: 'initial',
    disabledPermission: 'initial',
    deletePermission: 'initial',
  })

  const loading = computed(() => Object.values(nStatus).some(el => el === 'loading'))

  /**
   * Getters
   */
  const listFormatter = computed(() =>
    list.value
      .map((grupo) => ({
        id: grupo.id,
        active: grupo.productProfile.active,
        nome: grupo.productProfile.description
      }))
      .filter((el) => el.active)
  )

  const listPermissionsName = computed(() => permissions.value?.flatMap(el => el.access?.map(el => el.description?.toLowerCase())))

  // Set para consultas O(1) em canUserAccess/getFirstRouteAccess
  const listPermissionsNameSet = computed(() => new Set(listPermissionsName.value ?? []))

  const usuarioStore = useUsuariosStore()

  /**
   * Actions
   */
  const create = async (dataObj: GrupoPermissaoForm) => {
    nStatus.create = 'loading'
    try {
      const { success } = await gruposDePermissaoService.create(dataObj)
      if (success) {
        getAll()
        clearOptimizedList()
      }

      nStatus.create = 'success'
      return success
    } catch {
      nStatus.create = 'error'
    }
  }

  const update = async (dataObj: GrupoPermissaoForm, id: string) => {
    nStatus.update = 'loading'
    try {
      const { data, success } = await gruposDePermissaoService.update<GrupoPermissaoForm>(id, dataObj)
      if (success && data) {
        getAll()
        clearOptimizedList()
      }
      nStatus.update = 'success'
      return success
    } catch {
      nStatus.update = 'error'
    }
  }

  const getAll = async () => {
    nStatus.getAll = 'loading'
    try {
      const { data, success } = await gruposDePermissaoService.get()
      nStatus.getAll = 'success'
      // @TODO: tirar o map após ajuste no endpoit da aliare
      list.value = success
        ? data.data.map((el) => ({ ...el, productProfile: { ...el.productProfile, active: true } }))
        : []
    } catch {
      nStatus.getAll = 'error'
    }
  }

  // Verifica se já existe um requisicao de getOptimizedAll em andamento
  let getOptmisedListPromise: Promise<void> | null = null

  const CACHE_TTL = 300000

  /**
   * Retorna a lista de grupos de permissão de maneira otimizada (Com cache)
   * @param force ignora o cache e força uma nova requisição
   * @returns promise da requisição (em andamento ou recém iniciada)
   */
  const getOptimizedAll = (force?: boolean): Promise<void> => {
    // Já existe uma requisição em andamento: devolve a mesma promise
    if (newList.value.status === 1 && getOptmisedListPromise) return getOptmisedListPromise

    // Usa o cache se a última atualização foi a menos de 5 minutos
    const isFresh = newList.value.lastUpdate && Date.now() - newList.value.lastUpdate < CACHE_TTL
    if (!force && isFresh) return getOptmisedListPromise ?? Promise.resolve()

    newList.value.status = 1
    newList.value.groups = []
    newList.value.users = []

    getOptmisedListPromise = gruposDePermissaoService.get()
      .then((response) => {
        const { data, success } = response

        if (success) {
          const rows = data.data ?? []
          const seenIds = new Set<string>()
          const groups: { id: any; name: any; active: any }[] = []
          const users: { permissionId: string; users: string[] }[] = []
          // Índice permissionId -> entrada em `users`, evita varredura O(n) a cada usuário
          const usersByPermission = new Map<string, { permissionId: string; users: string[] }>()

          for (let i = 0; i < rows.length; i++) {
            const gpInfo = rows[i]

            if (!seenIds.has(gpInfo.id)) {
              seenIds.add(gpInfo.id)
              groups.push({
                id: gpInfo.id,
                name: gpInfo?.productProfile?.description,
                active: gpInfo?.productProfile?.active
              })
            }

            const usersProfiles = gpInfo.usersProfiles ?? []
            for (let j = 0; j < usersProfiles.length; j++) {
              const upInfo = usersProfiles[j]
              if (!upInfo.active || upInfo.blocked || !upInfo.userId) continue

              const entry = usersByPermission.get(upInfo.permissionId)
              if (entry) {
                entry.users.push(upInfo.userId)
              } else {            // Agrupa a cada grupo de permissão os usuários que possuem a permissão
                const newEntry = { permissionId: upInfo.permissionId, users: [ upInfo.userId ] }
                usersByPermission.set(upInfo.permissionId, newEntry)
                users.push(newEntry)
              }
            }
          }

          // Atribui de uma vez, evitando passar pelo proxy reativo a cada push
          newList.value.groups = groups
          newList.value.users = users
        }

        newList.value.lastUpdate = Date.now()
        newList.value.status = 2
      })
      .catch(() => {
        newList.value.status = -1
      })

    return getOptmisedListPromise
  }

  const clearOptimizedList = () => {
    Object.assign(newList.value, {
      status: 0,
      groups: [],
      users: [],
      lastUpdate: 0
    })
  }

  // Índice reverso userId -> grupos, calculado uma vez por mudança da lista otimizada
  const groupsByUser = computed(() => {
    const groups = newList.value.groups ?? []
    const users = newList.value.users ?? []
    const groupById = new Map(groups.map((group) => [group.id, group]))
    const index = new Map<string, { id: any; name: any; active: any }[]>()

    for (const entry of users) {
      const group = groupById.get(entry.permissionId)
      if (!group) continue

      for (const userId of entry.users) {
        const userGroups = index.get(userId)
        if (userGroups) userGroups.push(group)
        else index.set(userId, [ group ])
      }
    }

    return index
  })

  const getGroupByUser = (userId: string) => groupsByUser.value.get(userId) ?? []

  const remove = async (id: string) => {
    nStatus.delete = 'loading'
    try {
      const { success } = await gruposDePermissaoService.remove(id)
      if (success) {
        const index = list.value.findIndex((el) => el.id === id)
        if (index >= 0) list.value.splice(index, 1)
        clearOptimizedList()
      }

      nStatus.delete = 'success'
      return success
    } catch {
      nStatus.delete = 'error'
    }
  }

  const getById = async (id: string): Promise<GrupoPermissaoType> => {
    nStatus.getById = 'loading'
    try {
      const { data } = await gruposDePermissaoService.get(id)
      nStatus.getById = 'success'
      return data as GrupoPermissaoType
    } catch {
      nStatus.getById = 'error'
    }
  }

  const iteration = ref(0)

  const getUserPermissions = async (userId: string) => {
    nStatus.getUserPermissions = 'loading'
    permissions.value = []
    webStorage.setItem('permissions', permissions.value, true)

    try {
      const { data } = await gruposDePermissaoService.getPermissionFromUser(userId)
      
      iteration.value += 1

      if (data && data.length > 0) {
        permissions.value = data
        webStorage.setItem('permissions', permissions.value, true)
        nStatus.getUserPermissions = 'success'
      } else {
        nStatus.getUserPermissions = 'error'
      }

      return data
    } catch (error) {
      nStatus.getUserPermissions = 'error'
    }
  }

  const canUserAccess = (permission: string | string[], parent?: string): boolean => {
    if (!permission)
      return true

    const exclusiveElevatePrivileges = ['Regras Gerais', 'Contratos', 'Empresas', 'Painéis Dashboard']
    const exclusiveClient = ['Meu Contrato']
    const authStore = useAuthStore()

    if (!authStore.inAdminMode && exclusiveElevatePrivileges.includes(permission as string))
      return false

    if (authStore.inAdminMode && exclusiveClient.includes(permission as string))
      return false

    if (Array.isArray(permission)) {
      return permission.some(p => canUserAccess(p, parent))
    }

    if (typeof permission === 'string' && permission.includes('|')) {
      return permission.split('|').some(p => canUserAccess(p.trim(), parent))
    }

    const permissionSet = listPermissionsNameSet.value

    if (parent)
      return permissionSet.has(permission?.toLowerCase()) && permissionSet.has(parent?.toLowerCase())

    return permissionSet.has(permission?.toLowerCase())
  }

  const getFirstRouteAccess = (router: Router) => {
    const treeFuncs = router.options.routes.find((rt) => rt.name === 'main')?.children || []
    const elements = convertTreeRouterToArr(treeFuncs, [], null)
    return elements.find((el) => listPermissionsNameSet.value.has(el.name))
  }

  const isPermissionsEmpty = (): boolean => !permissions.value || permissions.value?.length === 0

  const setPermissions = () => (permissions.value = webStorage.getItem('permissions') ?? [])

  const setUsersPermission = async (idGrupoPermissao: string, usersId: string[]) => {
    nStatus.setPermission = 'loading'
    try {
      const success = await gruposDePermissaoService.sendBondPermissionUsers(idGrupoPermissao, usersId)
      if (success) {
        usuarioStore.pagedList.list
          .filter((el) => usersId.includes(el.id))
          .forEach((user) => {
            Object.assign(user, { ...user, grupoPermissao: idGrupoPermissao, isMissingData: false })
          })
      }
      nStatus.setPermission = 'success'
    } catch (error) {
      nStatus.setPermission = 'error'
    }
  }

  const disablePermissionUsers = async (usersAndPermissionsId: { userId: string; permissionId: string }[]) => {
    nStatus.disabledPermission = 'loading'
    try {
      const result = await gruposDePermissaoService.sendDisablePermissionUsers(usersAndPermissionsId)
      nStatus.disabledPermission = 'success'
      return result
    } catch {
      nStatus.disabledPermission = 'error'
    }
  }

  const deleteBulking = async (usersAndPermissionsId: { userId: string; permissionId: string }[]) => {
    nStatus.deletePermission = 'loading'
    try {
      const { data } = await permissionGroupService.deleteBulking(usersAndPermissionsId)

      nStatus.deletePermission = 'success'
      return data
    } catch {
      nStatus.deletePermission = 'error'
    }
  }

  return {
    nStatus,
    list,
    optimizedList: newList,
    permissions,
    loading,
    // Getters
    listFormatter,
    listPermissionsName,
    listPermissionsNameSet,
    iteration: computed(() => iteration.value),
    // Actions
    create,
    update,
    getAll,
    getOptimizedAll,
    getById,
    getGroupByUser,
    remove,
    clearOptimizedList,
    getUserPermissions,
    canUserAccess,
    getFirstRouteAccess,
    isPermissionsEmpty,
    setPermissions,
    setUsersPermission,
    disablePermissionUsers,
    deleteBulking,
  }
})

export default useGrupoDePermissoes